import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaLock, FaFileInvoiceDollar, FaEnvelope, FaSyncAlt } from 'react-icons/fa';
import PageAnimation from './PageAnimation';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useAbonnement } from '../../hooks/useAbonnement';

// Chemins accessibles même échu : sans eux, le client ne pourrait plus régler sa facture.
const CHEMINS_AUTORISES = ['/mon-abonnement', '/login', '/logout'];

/**
 * Remplace le contenu par un écran explicite quand l'abonnement est échu.
 *
 * Sans cet écran, le serveur renvoie des 402 et l'utilisateur reste devant des
 * erreurs muettes. Le contenu n'est jamais bloqué en attendant la vérification :
 * s'afficher un instant puis basculer vaut mieux qu'un écran de chargement.
 */
const SubscriptionGate = ({ children, role = 'admin-rh' }) => {
  const { isDark } = useTheme();
  const { estExpire, dateExpiration } = useAbonnement();
  const location = useLocation();
  const navigate = useNavigate();

  const exempte = CHEMINS_AUTORISES.some((c) => location.pathname.startsWith(c));
  if (!estExpire || exempte) return children;

  const peutPayer = role !== 'employee';
  const echeance = dateExpiration
    ? new Date(dateExpiration).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-10 ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
      <PageAnimation>
        <div
          className={`max-w-xl mx-auto rounded-2xl shadow-xl p-8 md:p-10 text-center ${
            isDark ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-800'
          }`}
        >
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
              isDark ? 'bg-red-900/40' : 'bg-red-100'
            }`}
          >
            <FaLock className={`w-9 h-9 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
          </div>

          <h1 className="text-2xl font-bold mb-3">Abonnement arrivé à échéance</h1>

          <p className={`mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            L&apos;accès aux données de votre entreprise est suspendu jusqu&apos;au règlement.
            Vos données sont conservées intactes.
          </p>

          {echeance ? (
            <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Échéance : {echeance}
            </p>
          ) : (
            <div className="mb-6" />
          )}

          {peutPayer ? (
            <>
              <button
                type="button"
                onClick={() => navigate('/mon-abonnement')}
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
              >
                <FaFileInvoiceDollar className="w-4 h-4" />
                Régler mon abonnement
              </button>

              <p className={`text-sm mt-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Consultez vos factures et transmettez votre preuve de paiement depuis
                « Mon abonnement ». L&apos;accès est rétabli dès la validation du règlement.
              </p>
            </>
          ) : (
            <div
              className={`rounded-xl p-4 text-sm text-left ${
                isDark ? 'bg-gray-700/60 text-gray-200' : 'bg-blue-50 text-blue-900'
              }`}
            >
              <p className="flex items-start gap-3">
                <FaEnvelope className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Prévenez l&apos;administrateur RH de votre entreprise : lui seul peut
                  renouveler l&apos;abonnement. Vos pointages et vos données sont conservés.
                </span>
              </p>
            </div>
          )}

          <button
            type="button"
            onClick={() => window.location.reload()}
            className={`inline-flex items-center gap-2 mt-6 text-sm transition-colors ${
              isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FaSyncAlt className="w-3 h-3" />
            J&apos;ai réglé, actualiser
          </button>
        </div>
      </PageAnimation>
    </div>
  );
};

export default SubscriptionGate;
